import { Box, IconButton, Paper, Slider, Stack, Tooltip, Typography } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import RepeatIcon from '@mui/icons-material/Repeat';
import RepeatOneIcon from '@mui/icons-material/RepeatOne';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { formatDuration } from '../../api/music';
import { useMusicPlayer } from './MusicPlayerContext';

export const PlayerControls = ({ compact = false }: { compact?: boolean }) => {
    const { t } = useTranslation();
    const player = useMusicPlayer();
    const { current, isPlaying, position, duration, volume, shuffle, repeat } = player;
    const total = duration || current?.duration_seconds || 0;
    const nextRepeat = repeat === 'off' ? 'all' : repeat === 'all' ? 'one' : 'off';

    return (
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={compact ? 1 : 2} alignItems={{ md: 'center' }} sx={{ width: '100%' }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, flex: compact ? '0 1 260px' : '0 1 320px' }}>
                <MusicNoteIcon color={current ? 'primary' : 'disabled'} />
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" noWrap fontWeight={600}>
                        {current ? current.title : t('music.nothingPlaying')}
                    </Typography>
                    {current?.artist && (
                        <Typography variant="caption" color="text.secondary" noWrap component="div">{current.artist}</Typography>
                    )}
                </Box>
            </Stack>

            <Stack direction="row" spacing={0.5} alignItems="center">
                <Tooltip title={t('music.shuffle')}>
                    <IconButton size="small" color={shuffle ? 'primary' : 'default'} onClick={() => player.setShuffle(!shuffle)}>
                        <ShuffleIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title={t('music.previous')}>
                    <span><IconButton onClick={player.prev} disabled={!current}><SkipPreviousIcon /></IconButton></span>
                </Tooltip>
                <Tooltip title={isPlaying ? t('music.pause') : t('music.play')}>
                    <span>
                        <IconButton color="primary" onClick={player.toggle} disabled={!current} sx={{ border: 1, borderColor: 'divider' }}>
                            {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title={t('music.next')}>
                    <span><IconButton onClick={player.next} disabled={!current}><SkipNextIcon /></IconButton></span>
                </Tooltip>
                <Tooltip title={`${t('music.repeat')}: ${repeat}`}>
                    <IconButton size="small" color={repeat !== 'off' ? 'primary' : 'default'} onClick={() => player.setRepeat(nextRepeat)}>
                        {repeat === 'one' ? <RepeatOneIcon fontSize="small" /> : <RepeatIcon fontSize="small" />}
                    </IconButton>
                </Tooltip>
            </Stack>

            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 160 }}>
                <Typography variant="caption" sx={{ minWidth: 40, textAlign: 'right' }}>{formatDuration(position)}</Typography>
                <Slider
                    size="small"
                    min={0}
                    max={Math.max(total, 1)}
                    step={0.5}
                    value={Math.min(position, Math.max(total, 1))}
                    disabled={!current}
                    onChange={(_event, value) => player.seek(Array.isArray(value) ? value[0] : value)}
                    aria-label="seek"
                />
                <Typography variant="caption" sx={{ minWidth: 40 }}>{formatDuration(total)}</Typography>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center" sx={{ width: compact ? 140 : 180 }}>
                <IconButton size="small" onClick={() => player.setVolume(volume > 0 ? 0 : 0.8)} aria-label={t('music.volume')}>
                    {volume > 0 ? <VolumeUpIcon fontSize="small" /> : <VolumeOffIcon fontSize="small" />}
                </IconButton>
                <Slider
                    size="small"
                    min={0}
                    max={1}
                    step={0.01}
                    value={volume}
                    onChange={(_event, value) => player.setVolume(Array.isArray(value) ? value[0] : value)}
                    aria-label={t('music.volume')}
                />
            </Stack>
        </Stack>
    );
};

/** Persistent mini player at the bottom of every page while something is queued. */
export const MiniPlayer = () => {
    const { current } = useMusicPlayer();
    const location = useLocation();
    if (!current || location.pathname.startsWith('/music')) {
        return null;
    }
    return (
        <>
            <Box sx={{ height: 72 }} />
            <Paper
                elevation={8}
                square
                sx={{ position: 'fixed', left: 0, right: 0, bottom: 0, px: 2, py: 1, zIndex: (theme) => theme.zIndex.appBar }}
            >
                <Stack direction="row" spacing={1} alignItems="center">
                    <Box sx={{ flex: 1, minWidth: 0 }}><PlayerControls compact /></Box>
                    <IconButton component={Link} to="/music" size="small" aria-label="music">
                        <MusicNoteIcon fontSize="small" />
                    </IconButton>
                </Stack>
            </Paper>
        </>
    );
};
